/**
 * Service Request Operations Handler
 */

import { supabase } from '../../supabase';
import { ParsedCommand, ExecutionResult } from '../types';

export async function executeServiceRequestOperation(
  command: ParsedCommand,
  userId: string
): Promise<ExecutionResult> {
  const { intent, entities } = command;

  try {
    switch (intent) {
      case 'query_service_requests':
        return await queryServiceRequests(entities.filters);

      case 'update_service_request':
        if (!entities.target_id) {
          return {
            success: false,
            error: 'No service request ID provided',
            message: 'Please specify which service request to update',
          };
        }
        return await updateServiceRequest(entities.target_id, entities.updates, userId);

      case 'create_service_request':
        return await createServiceRequest(entities.new_record, userId);

      default:
        return {
          success: false,
          error: `Unsupported service request operation: ${intent}`,
          message: 'Operation not supported',
        };
    }
  } catch (error) {
    console.error('Error in service request operation:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      message: 'Failed to execute service request operation',
    };
  }
}

async function queryServiceRequests(filters?: Record<string, any>): Promise<ExecutionResult> {
  let query = supabase
    .from('service_requests')
    .select(`
      id,
      title,
      description,
      status,
      priority,
      created_at,
      completed_at,
      home:homes(
        id,
        address,
        city,
        state,
        owner:profiles(full_name)
      )
    `)
    .order('created_at', { ascending: false });

  // Apply filters
  if (filters) {
    if (filters.status) {
      if (Array.isArray(filters.status)) {
        query = query.in('status', filters.status);
      } else {
        query = query.eq('status', filters.status);
      }
    }

    if (filters.priority) {
      if (Array.isArray(filters.priority)) {
        query = query.in('priority', filters.priority);
      } else {
        query = query.eq('priority', filters.priority);
      }
    }

    if (filters.home_id) {
      query = query.eq('home_id', filters.home_id);
    }
  }

  const { data, error } = await query.limit(50);

  if (error) {
    throw error;
  }

  return {
    success: true,
    data,
    message: `Found ${data?.length || 0} service request${data?.length !== 1 ? 's' : ''}`,
    affectedRecords: data?.length || 0,
    recordDetails: data,
  };
}

async function updateServiceRequest(
  id: string,
  updates?: Record<string, any>,
  userId?: string
): Promise<ExecutionResult> {
  if (!updates || Object.keys(updates).length === 0) {
    return {
      success: false,
      error: 'No updates provided',
      message: 'Please specify what to update',
    };
  }

  // Fetch current record
  const { data: current, error: fetchError } = await supabase
    .from('service_requests')
    .select('id, title, status')
    .eq('id', id)
    .single();

  if (fetchError || !current) {
    return {
      success: false,
      error: 'Service request not found',
      message: `Service request ${id} not found`,
    };
  }

  // Perform update
  const { data, error } = await supabase
    .from('service_requests')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return {
    success: true,
    data,
    message: `Successfully updated service request "${current.title}"`,
    affectedRecords: 1,
    recordDetails: [data],
  };
}

async function createServiceRequest(
  newRecord?: Record<string, any>,
  userId?: string
): Promise<ExecutionResult> {
  if (!newRecord) {
    return {
      success: false,
      error: 'No data provided',
      message: 'Please specify the service request details',
    };
  }

  // Ensure required fields
  if (!newRecord.title) {
    return {
      success: false,
      error: 'Missing required field: title',
      message: 'Please provide a title for the service request',
    };
  }

  // Set defaults
  const recordData = {
    title: newRecord.title,
    description: newRecord.description || '',
    status: newRecord.status || 'pending',
    priority: newRecord.priority || 'normal',
    home_id: newRecord.home_id || null,
    owner_id: newRecord.owner_id || userId,
  };

  const { data, error } = await supabase
    .from('service_requests')
    .insert(recordData)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return {
    success: true,
    data,
    message: `Successfully created service request "${data.title}"`,
    affectedRecords: 1,
    recordDetails: [data],
  };
}
